/**
 * Patient devices — meters, and the readings they send.
 *
 * Replaces `src/api/deviceApi.js`, `AddPatientInfo.AddPatientDevice`,
 * `AddPatientInfo.AddPatientInformation` (the Fora enrolment half of it),
 * `RemovePatientInfo.RemovePatientDevice`, `GetPatientInfo.GetPatientDevice` and
 * `GetPatientInfo.GetNebulizerData`.
 *
 * `GetPatientDevice` answered a Fora XML blob inside a JSON string, which the
 * SPA ran `xml2js` over in the browser; `listPatientDevices` answers rows.
 *
 * The meter is inventory and its measurements are clinical data, so the two are
 * gated differently: the device list is `devices:read`, the readings are
 * `vitals:read`. The chart-wide measurement history is `vitals.js`.
 *
 * The webhooks that ingest readings from a device fleet — the signed gateway
 * and Fora's own push — are not in this module and are not callable from a
 * browser: they authenticate a machine, not a person.
 */

import api from './client';
import { meterRowsFromDevices } from '@/utils/meterRows';

// --- devices on a chart ----------------------------------------------------

/**
 * `?filter[type]`, `[manufacturer_code]`, `[is_active]`. The chart's meters
 * only; `filter[removal]=owed` also lists a meter taken off the chart whose
 * removal from Fora is still owed (deviceBridge OWED_REMOVAL — the device
 * page asks, a list of meters to bill or read must not).
 */
export function listPatientDevices(patientId, params = {}) {
  return api.list(`/patients/${patientId}/devices`, params);
}

/**
 * Every meter on the chart, read to the last page.
 *
 * The CPT page offers each meter for enrolment under a code; a chart can hold
 * more than one page of them (the API pages at 25 by default), and a meter on
 * page two that is not in the picker is a meter that cannot be assigned. A page
 * that fails fails the whole read: the pages in hand are not a list the screen
 * may show as complete.
 */
export async function listAllPatientDevices(patientId, params = {}) {
  const items = [];

  // Bounded: 200 pages of 100 is far past any chart, and a loop that could not
  // end is not a list.
  for (let page = 1; page <= 200; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await listPatientDevices(patientId, { ...params, page, per_page: 100 });
    items.push(...(result.items || []));

    const last = result.meta && Number(result.meta.last_page);
    if (!last || page >= last) break;
  }

  return { items, meta: null, links: null };
}

// The legacy pages' meter rows (utils/meterRows.js) are re-exported here: they are
// this module's rows, read the way the XML parse used to leave them.
export { meterRowsFromDevices };

export function getPatientDevice(patientId, deviceId) {
  return api.get(`/patients/${patientId}/devices/${deviceId}`);
}

/**
 * `source` is a `data_sources.code` — `fora_device` for a Fora meter, whose
 * `type` must be a catalogue model code (listDeviceModels). The answer carries
 * `vendor_sync`: Fora's half runs on the server's queue, so it starts
 * `pending` and the device list shows how it ended.
 */
export function addPatientDevice(patientId, payload) {
  return api.post(`/patients/${patientId}/devices`, payload);
}

export function removePatientDevice(patientId, deviceId) {
  return api.delete(`/patients/${patientId}/devices/${deviceId}`);
}

/** What one meter has sent. `?from=&to=`, `?filter[type]`, `[status]`. */
export function listDeviceReadings(patientId, deviceId, params = {}) {
  return api.list(`/patients/${patientId}/devices/${deviceId}/readings`, params);
}

// --- catalogue and therapy -------------------------------------------------

/**
 * The device models a chart can register — what UserDevices.vue hard-coded
 * as `deviceTypes`. `?filter[data_source]=fora_device` for Fora's.
 */
export function listDeviceModels(params = {}) {
  return api.list('/device-models', params);
}

/**
 * A chart's nebulizer sessions (Fora type 32): therapy shown with medication
 * administration, gated `medications:read`, never vitals. `?from=&to=`, `sort`.
 */
export function listNebulizerSessions(patientId, params = {}) {
  return api.list(`/patients/${patientId}/nebulizer-sessions`, params);
}

// --- bulk import -----------------------------------------------------------

/**
 * A CSV of readings. Five near-identical legacy routes collapse here and
 * `source` is what used to be the difference between them; all five read the
 * upload with no MIME, size or extension check. Multipart: `patient_id`,
 * `source` and one part called `file`.
 */
export function createDeviceImport(formData, onProgress) {
  return api.upload('/device-imports', formData, { onProgress });
}

/** Poll the import. The upload answers 202 and names this id. */
export function getDeviceImport(importId) {
  return api.get(`/device-imports/${importId}`);
}

export default {
  listPatientDevices, listAllPatientDevices, meterRowsFromDevices, getPatientDevice, addPatientDevice, removePatientDevice,
  listDeviceReadings, listDeviceModels, listNebulizerSessions,
  createDeviceImport, getDeviceImport,
};
